import { useIsFetching } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

export function useFetchProgress({
	initialWidth = 20,
	maxWidth = 90,
	step = 10,
	interval = 500,
	resetDelay = 200,
} = {}) {
	const isFetching = useIsFetching();
	const [progress, setProgress] = useState(0);
	const intervalRef = useRef<number | null>(null);
	const timeoutRef = useRef<number | null>(null);

	const clearTimers = useCallback(() => {
		if (intervalRef.current) clearInterval(intervalRef.current);
		if (timeoutRef.current) clearTimeout(timeoutRef.current);
		intervalRef.current = null;
		timeoutRef.current = null;
	}, []);

	useEffect(() => {
		clearTimers();

		if (isFetching) {
			setProgress(initialWidth);
			document.body.dataset.wait = 'true';

			intervalRef.current = window.setInterval(() => {
				setProgress((prev) => {
					if (prev >= maxWidth) {
						if (intervalRef.current) clearInterval(intervalRef.current);
						return maxWidth;
					}
					return prev + step;
				});
			}, interval);
		} else {
			setProgress(100);
			timeoutRef.current = window.setTimeout(() => setProgress(0), resetDelay);
			document.body.removeAttribute('data-wait');
		}

		return () => {
			clearTimers();
			document.body.removeAttribute('data-wait');
		};
	}, [
		isFetching,
		clearTimers,
		initialWidth,
		maxWidth,
		step,
		interval,
		resetDelay,
	]);

	return progress;
}
